import { useTokens } from './theme.jsx';

export function SkeletonBlock({ className = '' }) {
  const t = useTokens();
  return (
    <div
      aria-hidden="true"
      className={`animate-pulse rounded-md ${className}`}
      style={{ backgroundColor: t.statBg }}
    />
  );
}

function SkeletonStatus({ label, children, className = '' }) {
  return (
    <div role="status" aria-label={label} aria-busy="true" className={className}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

export function RowsSkeleton({ rows = 5, className = '' }) {
  return (
    <SkeletonStatus label="Loading rows" className={className}>
      {Array.from({ length: rows }, (_, index) => {
        return (
          <div key={index} className="flex min-h-[58px] items-center justify-between gap-6 border-b border-transparent py-2">
            <div className="min-w-0 flex-1">
              <SkeletonBlock className="h-4 w-2/5 min-w-[120px]" />
              <SkeletonBlock className="mt-2 h-3 w-3/5 min-w-[160px]" />
            </div>
            <SkeletonBlock className="h-4 w-24 shrink-0" />
          </div>
        );
      })}
    </SkeletonStatus>
  );
}

export function TableSkeleton({ rows = 5, columns = 4, className = '' }) {
  return (
    <SkeletonStatus label="Loading table" className={`overflow-hidden ${className}`}>
      <div className="grid gap-4 border-b border-transparent py-2" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
        {Array.from({ length: columns }, (_, index) => {
          return <SkeletonBlock key={index} className="h-3 w-3/5" />;
        })}
      </div>
      {Array.from({ length: rows }, (_, row) => {
        return (
          <div key={row} className="grid gap-4 border-b border-transparent py-3" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
            {Array.from({ length: columns }, (_, column) => {
              return <SkeletonBlock key={column} className={`h-4 ${column === 0 ? 'w-4/5' : 'w-3/5'}`} />;
            })}
          </div>
        );
      })}
    </SkeletonStatus>
  );
}

export function MetricsSkeleton({ count = 4, className = '' }) {
  return (
    <SkeletonStatus label="Loading metrics" className={`grid grid-cols-2 gap-2 lg:grid-cols-4 ${className}`}>
      {Array.from({ length: count }, (_, index) => {
        return (
          <div key={index} className="min-h-[80px] rounded-md border border-transparent p-3">
            <SkeletonBlock className="h-3 w-3/5" />
            <SkeletonBlock className="mt-3 h-7 w-2/5" />
          </div>
        );
      })}
    </SkeletonStatus>
  );
}

export function ChartSkeleton({ count = 1, className = '' }) {
  return (
    <SkeletonStatus label="Loading charts" className={className}>
      {Array.from({ length: count }, (_, index) => {
        return (
          <div key={index} className="mb-4 last:mb-0">
            <SkeletonBlock className="h-4 w-40" />
            <SkeletonBlock className="mt-3 h-52 w-full" />
          </div>
        );
      })}
    </SkeletonStatus>
  );
}

export function DetailSkeleton({ className = '' }) {
  return (
    <SkeletonStatus label="Loading details" className={className}>
      <SkeletonBlock className="h-8 w-64 max-w-full" />
      <SkeletonBlock className="mt-2 h-3 w-48 max-w-full" />
      <MetricsSkeleton count={4} className="mt-5" />
      <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <SkeletonBlock className="h-44 w-full" />
        <SkeletonBlock className="h-44 w-full" />
      </div>
    </SkeletonStatus>
  );
}
